import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Header } from '../components/ui/header';
import { Footer } from '../components/ui/footer';

export const PrivacyPolicy: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || i18n.language || 'en';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      title: t('privacyPolicy.sections.noDataStorage.title'),
      content: t('privacyPolicy.sections.noDataStorage.content', { returnObjects: true }) as string[]
    },
    {
      title: t('privacyPolicy.sections.noRestrictions.title'),
      content: t('privacyPolicy.sections.noRestrictions.content', { returnObjects: true }) as string[]
    },
    {
      title: t('privacyPolicy.sections.dataCollection.title'),
      content: t('privacyPolicy.sections.dataCollection.content', { returnObjects: true }) as string[]
    },
    {
      title: t('privacyPolicy.sections.dataUsage.title'),
      content: t('privacyPolicy.sections.dataUsage.content', { returnObjects: true }) as string[]
    },
    {
      title: t('privacyPolicy.sections.thirdParty.title'),
      content: t('privacyPolicy.sections.thirdParty.content', { returnObjects: true }) as string[]
    },
    {
      title: t('privacyPolicy.sections.security.title'),
      content: t('privacyPolicy.sections.security.content', { returnObjects: true }) as string[]
    },
    {
      title: t('privacyPolicy.sections.changes.title'),
      content: t('privacyPolicy.sections.changes.content', { returnObjects: true }) as string[]
    },
    {
      title: t('privacyPolicy.sections.contact.title'),
      content: t('privacyPolicy.sections.contact.content', { returnObjects: true }) as string[]
    }
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Helmet>
        <title>{t('privacyPolicy.title')} | Scormate</title>
        <meta name="description" content={t('privacyPolicy.subtitle')} />
        <link rel="canonical" href={`https://scormate.com/${currentLang}/privacy-policy`} />
      </Helmet>

      <Header />

      <main id="main" className="flex-grow py-14 px-4 sm:px-8" role="main">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-10"
          >
            <h1 className="text-[26px] font-semibold tracking-[-0.025em] mb-3">
              {t('privacyPolicy.title')}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t('privacyPolicy.subtitle')}
            </p>
          </motion.div>

          <div className="space-y-8">
            {sections.map((section, index) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-panel border border-border rounded-md p-6"
              >
                <h2 className="text-[17px] font-semibold mb-3">
                  {section.title}
                </h2>
                <div className="space-y-4">
                  {section.content.map((paragraph, pIndex) => (
                    <p key={pIndex} className="text-[13px] leading-relaxed text-muted-foreground">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.8 }}
            className="mt-10 text-xs text-faint"
          >
            <p className="italic">
              {t('privacyPolicy.footer.effectiveDate')}
            </p>
            <p className="mt-4">
              {t('privacyPolicy.footer.closing')}
            </p>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;